import { CompanySettlementsMapper } from "@/adapters/CompanySettlements.mapper";
import { CORP_INVOICE_COLUMNS, CORP_LEDGER_COLUMNS } from "@/types/company/settlements.dto";
import { toB2BError, toDataShapeError } from "@/utils/b2bErrors";
import { lazyService } from "@/utils/lazyMock";
import { supabase } from "@/utils/supabase";
export class CompanySettlementsService {
    async listInvoices(companyId) {
        const { data, error } = await supabase()
            .from("v_corp_invoices")
            .select(CORP_INVOICE_COLUMNS)
            .eq("company_id", companyId)
            .order("issue_date", { ascending: false });
        if (error)
            throw toB2BError(error);
        try {
            return (data ?? []).map(CompanySettlementsMapper.invoiceToDomain);
        }
        catch (parseError) {
            throw toDataShapeError(parseError);
        }
    }
    async listInvoiceLines(invoiceId) {
        const { data, error } = await supabase()
            .from("v_corp_invoice_lines")
            .select("*")
            .eq("invoice_id", invoiceId)
            .order("line_date");
        if (error)
            throw toB2BError(error);
        try {
            return (data ?? []).map(CompanySettlementsMapper.lineToDomain);
        }
        catch (parseError) {
            throw toDataShapeError(parseError);
        }
    }
    async listPayments(companyId) {
        const { data, error } = await supabase()
            .from("v_corp_payments")
            .select("*")
            .eq("company_id", companyId)
            .order("paid_at", { ascending: false });
        if (error)
            throw toB2BError(error);
        try {
            return (data ?? []).map(CompanySettlementsMapper.paymentToDomain);
        }
        catch (parseError) {
            throw toDataShapeError(parseError);
        }
    }
    async listLedger(companyId) {
        const { data, error } = await supabase()
            .from("v_corp_ledger")
            .select(CORP_LEDGER_COLUMNS)
            .eq("company_id", companyId)
            .order("effective_at", { ascending: false });
        if (error)
            throw toB2BError(error);
        try {
            return (data ?? []).map(CompanySettlementsMapper.ledgerToDomain);
        }
        catch (parseError) {
            throw toDataShapeError(parseError);
        }
    }
    async registerPayment(companyId, input, idempotencyKey) {
        const { data, error } = await supabase().rpc("corp_payment_register", CompanySettlementsMapper.toPaymentRegisterParams(companyId, input, idempotencyKey));
        if (error)
            throw toB2BError(error);
        try {
            return CompanySettlementsMapper.paymentRegisterResultToDomain(data);
        }
        catch (parseError) {
            throw toDataShapeError(parseError);
        }
    }
    async voidPayment(paymentId, reason) {
        const { data, error } = await supabase().rpc("corp_payment_void", {
            p_payment_id: paymentId,
            p_reason: reason,
        });
        if (error)
            throw toB2BError(error);
        try {
            return CompanySettlementsMapper.paymentVoidResultToDomain(data);
        }
        catch (parseError) {
            throw toDataShapeError(parseError);
        }
    }
    async createAdjustment(companyId, input, idempotencyKey) {
        const { data, error } = await supabase().rpc("corp_adjustment_create", CompanySettlementsMapper.toAdjustmentParams(companyId, input, idempotencyKey));
        if (error)
            throw toB2BError(error);
        try {
            return CompanySettlementsMapper.adjustmentResultToDomain(data);
        }
        catch (parseError) {
            throw toDataShapeError(parseError);
        }
    }
    async adjustOrder(orderId, actualKwh, reason) {
        const { data, error } = await supabase().rpc("corp_order_adjust", {
            p_order_id: orderId,
            p_actual_kwh: actualKwh,
            p_reason: reason,
        });
        if (error)
            throw toB2BError(error);
        try {
            return CompanySettlementsMapper.orderAdjustResultToDomain(data);
        }
        catch (parseError) {
            throw toDataShapeError(parseError);
        }
    }
    async generateInvoices(companyId, asOf) {
        const { data, error } = await supabase().rpc("corp_generate_invoices", {
            p_as_of: asOf,
            p_company_id: companyId,
        });
        if (error)
            throw toB2BError(error);
        try {
            return CompanySettlementsMapper.runResultToDomain(data);
        }
        catch (parseError) {
            throw toDataShapeError(parseError);
        }
    }
    async voidInvoice(invoiceId, reason) {
        const { data, error } = await supabase().rpc("corp_invoice_void", {
            p_invoice_id: invoiceId,
            p_reason: reason,
        });
        if (error)
            throw toB2BError(error);
        try {
            return CompanySettlementsMapper.invoiceVoidResultToDomain(data);
        }
        catch (parseError) {
            throw toDataShapeError(parseError);
        }
    }
    async setExternalDoc(invoiceId, input) {
        const { data, error } = await supabase().rpc("corp_invoice_set_external_doc", CompanySettlementsMapper.toExternalDocParams(invoiceId, input));
        if (error)
            throw toB2BError(error);
        try {
            return CompanySettlementsMapper.invoiceExternalDocResultToDomain(data);
        }
        catch (parseError) {
            throw toDataShapeError(parseError);
        }
    }
}
// Литерал REACT_APP_B2B_MOCK — см. CompanyLinkService.ts / utils/lazyMock.ts (G4).
export const companySettlementsService = process.env.REACT_APP_B2B_MOCK === "true"
    ? lazyService(() => import("@/services/mock/CompanySettlementsServiceMock").then((module) => module.companySettlementsServiceMock))
    : new CompanySettlementsService();
